/* eslint-disable */
import { Input, Typography } from 'antd';
import { FormInstance } from 'antd/lib/form';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { get } from 'lodash';
import React, { useLayoutEffect } from 'react';

dayjs.extend(utc);
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  Button,
  Checkbox,
  DateInput,
  Form,
  PhoneNumberFormItem,
  SalesFilterSelect,
  Select,
} from '~/components';
import { useHandbook } from '~/controllers/handbook';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import { AgentLeadPayload, AgentLeadStatus } from '~/types/AgentLead';
import { CompanyTypeReq } from '~/types/CompanyType';
import { NameFn } from '~/utils/buildNamePath';
import createUid from '~/utils/createUid';
import { handleCompanyType } from '~/utils/handleCompanyType';
import { maxLength } from '~/utils/lengthValidation';
import phoneValidation from '~/utils/phoneValidation';
import phoneValidationWithPrefix from '~/utils/phoneValidationWithPrefix';
import { textOnlyValidation } from '~/utils/textOnlyValidation';
import { validateCharacters } from '~/utils/validateCharacters';

import EmailFormItem from '../../../components/inputs/EmailFormItem';
import ApplicationIdInput from '~/components/ApplicationIdInput';

interface Props {
  onFinish?: (values: AgentLeadPayload) => any;
  n: NameFn;
  disabled?: boolean;
  form: FormInstance<AgentLeadPayload>;
  status?: AgentLeadStatus;
  rejectionReason?: string;
  initialValues?: any;
  edit?: boolean;
  formName?: string;
}

const LeadForm: React.FC<Props> = (props) => {
  const {
    onFinish,
    n,
    disabled = false,
    form,
    status,
    initialValues,
    edit,
    formName: customFormName,
  } = props;

  const { t } = useTranslation('sales');
  const { t: tAgent } = useTranslation('agent');
  const handbookQuery = useHandbook();
  const handbook = handbookQuery.data?.handbooks;

  const onValuesChange = (changedValues: CompanyTypeReq, formValues: any) => {
    if (!handbook) return;
    handleCompanyType(form, handbook, n)(changedValues, formValues);
  };

  useLayoutEffect(() => {
    if (!edit || !initialValues) {
      return () => {
        if (!edit) return;
        form.resetFields();
      };
    }

    // Transform products: map API product fields to form checkboxes
    const transformedProducts: any = {};
    if (initialValues?.products) {
      // Map ECOM or NGenius Online to 'ECOM' checkbox
      if (initialValues.products['ECOM'] || initialValues.products['NGenius Online'] || initialValues.products['ngeniusOnline']) {
        transformedProducts['ECOM'] = true;
      }
      // Map Pay Links to 'Pay Links' checkbox
      if (initialValues.products['Pay Links']) {
        transformedProducts['Pay Links'] = true;
      }
      // Map POS to 'POS' checkbox
      if (initialValues.products['POS'] || initialValues.products['pos']) {
        transformedProducts['POS'] = true;
      }
      // Map Other to 'Other' checkbox
      if (initialValues.products['Other']) {
        transformedProducts['Other'] = true;
      }
    }

    // Transform phone number: strip +966 or 966 prefix for display
    let displayPhoneNumber = initialValues?.phoneNumber || '';
    if (displayPhoneNumber) {
      // Strip +966 prefix
      if (displayPhoneNumber.startsWith('+966')) {
        displayPhoneNumber = displayPhoneNumber.substring(4);
      }
      // Strip 966 prefix (without +)
      else if (displayPhoneNumber.startsWith('966')) {
        displayPhoneNumber = `+${displayPhoneNumber.substring(3)}`;
      }
    }

    // Transform date&time to YYYY-MM-DD HH:mm format
    let formattedDateTime = initialValues?.['date&time'];
    if (formattedDateTime) {
      formattedDateTime = dayjs(formattedDateTime).format('YYYY-MM-DD HH:mm');
    }
    let lastModificationDateTime = initialValues?.['lastModificationDate&Time'];
    if (lastModificationDateTime) {
      lastModificationDateTime = dayjs.utc(lastModificationDateTime).format('YYYY-MM-DD HH:mm');
    }
    // Transform channel value for display
    const transformedValues = {
      ...initialValues,
      applicationId: initialValues?.applicationId ?? initialValues?.applicationDisplayId,
      channel:
        initialValues?.channel === 'SOB_DROPPED_JOURNEY' ? 'SOB_JOURNEY' : initialValues?.channel,
      // Set phone number without +966 prefix
      phoneNumber: displayPhoneNumber,
      // Apply transformed products
      products: transformedProducts,
      // Apply formatted date&time
      'date&time': formattedDateTime,
      'lastModificationDate&Time': lastModificationDateTime,
      // Transform productConverted to array for multi-select if it's a string
      productConverted:
        typeof initialValues?.productConverted === 'string'
          ? initialValues.productConverted
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
          : initialValues?.productConverted || [],
    };
    form.setFieldsValue(transformedValues);
    return () => {
      form.resetFields();
    };
  }, [initialValues, edit, form]);

  // Transform channel value back to original before submitting
  const handleFinish = (values: any) => {
    // Add +966 prefix to phone number if it doesn't have a country code
    let phoneNumber = values.phoneNumber;
    if (phoneNumber && !phoneNumber.startsWith('+')) {
      phoneNumber = `+966${phoneNumber}`;
    }

    // Convert date&time from display format to ISO format for API
    let dateTime = values['date&time'];
    if (dateTime && typeof dateTime === 'string') {
      // If it's in display format (YYYY-MM-DD HH:mm), convert to ISO
      dateTime = dayjs(dateTime, 'YYYY-MM-DD HH:mm').toISOString();
    }

    // Convert lastModificationDateTime to ISO format for API
    // const lastModificationDateTime = dayjs().toISOString();

    // Explicitly pick only the 4 known product keys — strips out 'NGenius Online' and any residual old keys
    const rawProducts = values.products || {};
    const cleanProducts = {
      'ECOM': !!(rawProducts['ECOM'] || rawProducts['ecom'] || rawProducts["ngeniusOnline"] || rawProducts["NGenius Online"]),
      'Pay Links': !!(rawProducts['Pay Links'] || rawProducts['payLinks']),
      'POS': !!(rawProducts['POS'] || rawProducts['pos']),
      'Other': !!(rawProducts['Other'] || rawProducts['other']),
    };

    const transformedValues = {
      ...values,
      phoneNumber,
      'date&time': dateTime,
      // 'lastModificationDateTime':lastModificationDateTime,
      channel: values.channel === 'SOB_JOURNEY' ? 'SOB_DROPPED_JOURNEY' : values.channel,
      // Convert multi-select array back to comma-separated string for API if needed
      productConverted: Array.isArray(values.productConverted)
        ? values.productConverted.join(', ')
        : values.productConverted,
      products: cleanProducts,
    };
    onFinish?.(transformedValues);
  };

  // Define channel groups for conditional field visibility
  const CHANNELS_WITH_UTM_FIELDS = [
    'SOB_DROPPED_JOURNEY',
    'SOB_JOURNEY',
    'NI_WEBSITE_PRODUCT',
    'NI_WEBSITE_PRICE',
    'NI_WEBSITE_GENERAL',
  ];
  const CHANNELS_WITH_EXTENDED_FIELDS = [...CHANNELS_WITH_UTM_FIELDS, 'SOB_CONTACT_US'];

  const currentChannel = get(initialValues, 'channel');
  const showUtmFields = CHANNELS_WITH_UTM_FIELDS.includes(currentChannel);
  const showExtendedFields = CHANNELS_WITH_EXTENDED_FIELDS.includes(currentChannel);

  const leadId = Form.useWatch(n(['leadId']), form);

  // Watch status for conditional field visibility
  const currentStatus = Form.useWatch(n(['status']), form);
  const currentConversionDate = Form.useWatch(n(['conversionDate']), form);
  const leadDateTime = Form.useWatch(n(['date&time']), form);
  const showFollowUpDate = currentStatus === 'Contacted';
  const showConversionFields = currentStatus === 'Converted to Application';

  // Channels that allow editing of basic fields
  const EDITABLE_CHANNELS = ['Manual', 'MANUAL', 'SOB_DROPPED_JOURNEY'];
  const canEditBasicFields = EDITABLE_CHANNELS.includes(currentChannel);

  // Helper function to conditionally apply rules based on field disabled state
  const getRules = (rules: any[], isDisabled?: boolean) => {
    // If field is disabled, don't apply validation rules
    return isDisabled ? [] : rules;
  };

  // Auto-calculate Time to Conversion as (Conversion Date - Lead Date & Time) in days
  React.useEffect(() => {
    if (currentStatus === 'Converted to Application') {
      const leadDateTime = form.getFieldValue(n(['date&time']));
      const conversionDate = currentConversionDate;

      if (leadDateTime && conversionDate) {
        const leadAt = dayjs(leadDateTime, 'YYYY-MM-DD HH:mm');
        const convertedAt = dayjs(conversionDate);
        const daysDifference = convertedAt.diff(leadAt, 'day');

        // Set the calculated value in days
        form.setFieldValue(
          n(['timetoConversion']),
          `${daysDifference} day${daysDifference !== 1 ? 's' : ''}`
        );
      } else {
        // Clear the field if either date is missing
        form.setFieldValue(n(['timetoConversion']), undefined);
      }
    }
  }, [currentStatus, currentConversionDate, form, n]);

  // @ts-ignore
  return (
    <Container className="LeadAdd">
      <Form
        form={form}
        name={customFormName || formName}
        onFinish={handleFinish}
        onValuesChange={onValuesChange}
      >
        <Section>
          <Form.Item
            hidden
            name={n(['leadId'])}
            initialValue={get(initialValues, 'leadId') || createUid()}
          >
            <Input />
          </Form.Item>
          <InputsGroupLayout>
            {get(initialValues, 'leadId') && (
              <Form.Item
                name={n(['leadNo'])}
                disabled
                initialValue={`NI-${get(initialValues, 'leadId')}`}
                labelText={t('Lead No.')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            )}
            {(leadId || get(initialValues, 'leadId')) && (edit || disabled || status) && (
              <Form.Item
                name={n(['applicationId'])}
                labelText={tAgent('Application ID')}
                disabled
              >
                <ApplicationIdInput />
              </Form.Item>
            )}
            <Form.Item
              labelText={t('Channel')}
              name={n(['channel'])}
              initialValue={
                get(initialValues, 'channel') === 'SOB_DROPPED_JOURNEY'
                  ? 'SOB_JOURNEY'
                  : get(initialValues, 'channel') || 'Manual'
              }
              disabled
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            {edit && currentChannel === 'SOB_DROPPED_JOURNEY' && (
              <Form.Item
                labelText={t('Stage')}
                name={n(['stage'])}
                disabled
                rules={[{ required: true }]}
              >
                <SalesFilterSelect filterCriteria="stage" allowClear />
              </Form.Item>
            )}
            {edit && (
              <>
                <Form.Item
                  hidden={!showUtmFields}
                  labelText={t('UTM-Source')}
                  name={n(['utmSource'])}
                  disabled
                >
                  <SalesFilterSelect filterCriteria="utmSource" allowClear />
                </Form.Item>
                <Form.Item
                  hidden={!showUtmFields}
                  labelText={t('UTM-Medium')}
                  name={n(['utmMedium'])}
                  disabled
                >
                  <SalesFilterSelect filterCriteria="utmMedium" allowClear />
                </Form.Item>
              </>
            )}
            {edit && (
              <>
                <Form.Item
                  hidden={!showUtmFields}
                  labelText={t('Campaign Name')}
                  name={n(['campaignName'])}
                  disabled
                >
                  <SalesFilterSelect filterCriteria="campaignName" allowClear />
                </Form.Item>
                <Form.Item
                  hidden={!showUtmFields}
                  labelText={t('Ad Group Name')}
                  name={n(['adGroupName'])}
                  disabled
                >
                  <SalesFilterSelect filterCriteria="adGroupName" allowClear />
                </Form.Item>
              </>
            )}
            {edit && (
              <>
                <Form.Item
                  hidden={!showExtendedFields}
                  labelText={t('Landing Page')}
                  name={n(['landingPage'])}
                  disabled
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  hidden={!showExtendedFields}
                  labelText={t('Referral')}
                  name={n(['referralUrl'])}
                  disabled
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  hidden={!showExtendedFields}
                  labelText={t('Device')}
                  name={n(['device'])}
                  disabled
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  hidden={!showExtendedFields}
                  labelText={t('GeoLocation')}
                  name={n(['geoLocation'])}
                  disabled
                >
                  <Input />
                </Form.Item>
              </>
            )}
            {edit && currentChannel === 'Upload' && (
              <Form.Item labelText={t('Source Company')} name={n(['sourceCompany'])} disabled>
                <Input />
              </Form.Item>
            )}
            <Form.Item
              labelText={t('Company Name')}
              name={n(['companyName'])}
              rules={getRules(
                [
                  {
                    required: true,
                  },
                  {
                    validator: validateCharacters({ mustIncludesLetters: true, allowArabic: true }),
                  },
                  { validator: maxLength(100) },
                ],
                edit && !canEditBasicFields
              )}
              disabled={edit && !canEditBasicFields}
            >
              <Input />
            </Form.Item>
            <Form.Item
              labelText={t('First Name')}
              name={n(['firstName'])}
              disabled={edit && !canEditBasicFields}
              rules={getRules(
                [
                  { required: true },
                  {
                    validator: textOnlyValidation({
                      withInnerSpaces: true,
                      allowArabic: true,
                    }),
                  },
                  { validator: maxLength(40) },
                ],
                edit && !canEditBasicFields
              )}
            >
              <Input />
            </Form.Item>
            <Form.Item
              labelText={t('Last Name')}
              name={n(['lastName'])}
              disabled={edit && !canEditBasicFields}
              rules={getRules(
                [
                  { required: true },
                  {
                    validator: textOnlyValidation({
                      withInnerSpaces: true,
                      allowArabic: true,
                    }),
                  },
                  { validator: maxLength(60) },
                ],
                edit && !canEditBasicFields
              )}
            >
              <Input />
            </Form.Item>
            <EmailFormItem
              labelText="Business Email"
              name={n(['email'])}
              disabled={edit && !canEditBasicFields}
              rules={getRules(
                [
                  { required: true },
                  { type: 'email', message: t('Invalid email', { ns: 'validation' }) },
                ],
                edit && !canEditBasicFields
              )}
            />
            <Form.Item
              labelText="Business Phone No."
              name={n(['phoneNumber'])}
              disabled={edit && !canEditBasicFields}
              rules={getRules(
                [{ required: true }, { validator: phoneValidationWithPrefix }],
                edit && !canEditBasicFields
              )}
            >
              <Input addonBefore="+966" />
            </Form.Item>
            <Form.Item
              labelText={t('Est. Monthly Transaction Value [SAR]')}
              name={n(['monthlyTransactionValue'])}
              disabled={edit && !canEditBasicFields}
              rules={getRules([{ required: true }], edit && !canEditBasicFields)}
            >
              <Input />
            </Form.Item>
          </InputsGroupLayout>
          <Section>
            <Typography.Title level={4}>{t('Products').toUpperCase()}</Typography.Title>
            <Typography.Paragraph>
              <Trans ns="agent" defaults="Select the products that merchant is interested in" />
            </Typography.Paragraph>
            {[
              {
                id: 'leadAddProductPOS',
                name: n(['products', 'ECOM']),
                title: t('ECOM'),
                isDisabled: (formValues: AgentLeadPayload) => edit,
              },
              {
                id: 'leadAddProductECommerce',
                name: n(['products', 'Pay Links']),
                title: t('Plugins'),
                isDisabled: (formValues: AgentLeadPayload) => edit,
              },
              {
                id: 'leadAddProductPOSNew',
                name: n(['products', 'POS']),
                title: t('POS'),
                isDisabled: (formValues: AgentLeadPayload) => edit,
              },
              {
                id: 'leadAddProductSoftPOS',
                name: n(['products', 'Other']),
                title: t('Other'),
                isDisabled: (formValues: AgentLeadPayload) => edit,
              },
            ].map(({ id, name, title, isDisabled }) => {
              return (
                <Form.ShouldUpdate
                  key={id}
                  shouldUpdate={(prev, next) =>
                    Object.values(prev.products || {}).filter((enabled) => enabled).length !==
                    Object.values(next.products || {}).filter((enabled) => enabled).length
                  }
                >
                  {(newForm) => {
                    const formValues = newForm.getFieldsValue();
                    return (
                      <Form.Item
                        name={name}
                        valuePropName="checked"
                        className="LeadForm-AddProduct"
                        disabled={edit && !canEditBasicFields}
                      >
                        <Checkbox
                          onChange={() => {
                            void form.validateFields([n(['productsValidator'])]);
                          }}
                        >
                          {title}
                        </Checkbox>
                      </Form.Item>
                    );
                  }}
                </Form.ShouldUpdate>
              );
            })}
            <Form.Item
              name={n(['productsValidator'])}
              rules={[
                {
                  validator: () => {
                    // const products = form.getFieldValue(n(['products'])) || {};
                    // const hasSelectedProduct = Object.values(products).some((val) => val === true);
                    // if (!hasSelectedProduct) {
                    //   return Promise.reject(new Error(t('Please select at least one product')));
                    // }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <div style={{ display: 'none' }} />
            </Form.Item>
          </Section>
          {edit && (
            <Form.Item
              labelText={'Prospect Customer Comments'}
              name={n(['products', 'comments'])}
              disabled={!canEditBasicFields}
            >
              <Input.TextArea autoSize={{ minRows: 3 }} />
            </Form.Item>
          )}
        </Section>

        <Section>
          <InputsGroupLayout>
            <Form.Item
              labelText={t('Lead Date & Time')}
              name={n(['date&time'])}
              disabled
              rules={[{ type: 'date' }]}
              initialValue={
                edit
                  ? dayjs(get(initialValues, 'date&time')).format('YYYY-MM-DD HH:mm')
                  : dayjs().format('YYYY-MM-DD HH:mm')
              }
            >
              <Input disabled />
            </Form.Item>
            {edit && (
              <Form.Item
                labelText={t('Lead Status')}
                name={n(['status'])}
                rules={[{ required: true }]}
              >
                <Select
                  options={
                    [
                      // { value: 'Active', labelText: 'Active' },
                      { value: 'New', labelText: 'New' },
                      { value: 'Contacted', labelText: 'Contacted' },
                      { value: 'In Progress', labelText: 'In Progress' },
                      { value: 'Converted to Application', labelText: 'Converted to Application' },
                      { value: 'Rejected', labelText: 'Rejected' },
                    ] as any
                  }
                />
              </Form.Item>
            )}
            {edit && currentStatus === 'Rejected' && (
              <Form.Item
                labelText={t('Rejected Reason')}
                name={n(['rejectionReason'])}
                rules={[{ required: true, message: t('Please enter rejected reason') }]}
              >
                <Input placeholder={t('Enter rejected reason')} />
              </Form.Item>
            )}
            <Form.Item
              labelText={t('New/Exist')}
              name={n(['new/exist'])}
              rules={[{ required: true }]}
            >
              <Select
                options={
                  [
                    { value: 'New', labelText: 'New' },
                    { value: 'Exist', labelText: 'Exist' },
                  ] as any
                }
              />
            </Form.Item>
            <Form.Item
              labelText={t('Contacted Date')}
              name={n(['contactedDate'])}
            // rules={[{ required: true }]}
            >
              <DateInput disableDateRule="date <= today" />
            </Form.Item>
            {showFollowUpDate && (
              <Form.Item labelText={t('Follow Up Date')} name={n(['followUpDate'])}>
                <DateInput />
              </Form.Item>
            )}
            {edit && (
              <Form.Item
                labelText={t('Last Modification Date & Time')}
                name={n(['lastModificationDate&Time'])}
                disabled
                initialValue={dayjs().format('YYYY-MM-DD HH:mm')}
              >
                <Input />
              </Form.Item>
            )}
          </InputsGroupLayout>
        </Section>

        {edit && (
          <Section id="activityLogs">
            <Typography.Title level={4}>{t('Activities Log').toUpperCase()}</Typography.Title>
            <div className="activities-log-container">
              <Form.List name={n(['activityLogs'])}>
                {(fields, { add, remove }) => (
                  <>
                    <div className="activities-log-header">
                      <div className="activity-column">{t('Date')}</div>
                      <div className="activity-column">{t('Activity Type')}</div>
                      <div className="activity-column">{t('Comments')}</div>
                      <div className="activity-actions">
                        <Button
                          id="add-activity-header-btn"
                          type="primary"
                          shape="circle"
                          size="small"
                          className="add-activity-btn"
                          onClick={() => add()}
                        >
                          +
                        </Button>
                      </div>
                    </div>
                    {fields.map(({ key, name, ...restField }) => {
                      const activityLog = get(initialValues, ['activityLogs', name]);
                      const hasExistingData =
                        edit &&
                        activityLog &&
                        (activityLog.date || activityLog.activityType || activityLog.comments);

                      return (
                        <div key={key} className="activity-log-row">
                          <div className="activity-column">
                            <Form.Item
                              {...restField}
                              name={[name, 'date']}
                              rules={[{ required: false }]}
                            >
                              <DateInput disabled={hasExistingData} />
                            </Form.Item>
                          </div>
                          <div className="activity-column">
                            <Form.Item
                              {...restField}
                              name={[name, 'activityType']}
                              rules={[{ required: false }]}
                            >
                              <Select
                                placeholder="Types"
                                disabled={hasExistingData}
                                options={[
                                  { value: 'Call', label: 'Call' },
                                  { value: 'Email', label: 'Email' },
                                  { value: 'Meeting', label: 'Meeting' },
                                ]}
                              />
                            </Form.Item>
                          </div>
                          <div className="activity-column">
                            <Form.Item
                              {...restField}
                              name={[name, 'comments']}
                              rules={[{ required: false }]}
                            >
                              <Input placeholder="Comments" />
                            </Form.Item>
                          </div>
                          <div className="activity-actions">
                            <Button
                              id={`remove-activity-btn-${key}`}
                              type="text"
                              size="small"
                              onClick={() => remove(name)}
                              className="remove-activity-btn"
                              disabled={hasExistingData}
                            >
                              ×
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </Form.List>
            </div>
          </Section>
        )}

        <Section>
          <InputsGroupLayout>
            {showConversionFields && (
              <>
                <Form.Item labelText={t('Product Converted')} name={n(['productConverted'])}>
                  {/* [NGenius Online] [Pay Links] [POS] [Other] */}
                  <Select
                    mode="multiple"
                    options={
                      [
                        { value: 'NGenius Online', labelText: 'NGenius Online' },
                        { value: 'Pay Links', labelText: 'Pay Links' },
                        { value: 'POS', labelText: 'POS' },
                        { value: 'Other', labelText: 'Other' },
                      ] as any
                    }
                  />
                </Form.Item>
                <Form.Item labelText={t('MID')} name={n(['mid'])}>
                  <Input />
                </Form.Item>
                <Form.Item
                  labelText={t('Conversion Date')}
                  name={n(['conversionDate'])}
                  rules={[{ required: true, message: t('Please select a conversion date') }]}
                >
                  <DateInput
                    disableDateRule={(date) => {
                      const today = dayjs().endOf('day');
                      // Disable dates after today
                      if (date.isAfter(today)) return true;
                      // Disable dates before the lead date&time
                      if (leadDateTime) {
                        const leadDate = dayjs(leadDateTime, 'YYYY-MM-DD HH:mm').startOf('day');
                        if (date.isBefore(leadDate)) return true;
                      }
                      return false;
                    }}
                  />
                </Form.Item>
                <Form.Item labelText={t('Conversion Type')} name={n(['conversionType'])}>
                  <Select
                    options={
                      [
                        { value: 'Onboarding Completed', labelText: 'Onboarding Completed' },
                        { value: 'Sale Made', labelText: 'Sale Made' },
                      ] as any
                    }
                  />
                </Form.Item>
                <Form.Item
                  labelText={t('Time to Conversion')}
                  name={n(['timetoConversion'])}
                  disabled
                >
                  <Input />
                </Form.Item>
              </>
            )}
            <Form.Item labelText={t('Result')} name={n(['result'])} rules={[{ required: true }]}>
              <SalesFilterSelect filterCriteria={'result'} allowClear />
            </Form.Item>
          </InputsGroupLayout>
        </Section>

        <Section>
          {edit && (
            <Form.Item labelText={'Sales Remark'} name={n(['remarks'])}>
              <Input.TextArea autoSize={{ minRows: 1 }} />
            </Form.Item>
          )}
        </Section>
      </Form>
    </Container>
  );
};
export const formName = 'leadForm';

const Container = styled.div`
  .LeadForm-AddProduct {
    margin-bottom: 8px;

    span {
      font-weight: 500;
    }
  }

  .LeadAdd-TerminationReason {
    margin-bottom: 8px;
  }

  .LeadAdd-TerminationButton {
    margin-bottom: 24px;
  }

  .activities-log-container {
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    overflow: hidden;
  }

  .activities-log-header {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr auto;
    gap: 1px;
    background-color: #fafafa;
    border-bottom: 1px solid #d9d9d9;
    font-weight: 600;
    font-size: 14px;

    .activity-column {
      padding: 12px 16px;
      border-right: 1px solid #d9d9d9;
      display: flex;
      align-items: center;

      &:last-child {
        border-right: none;
      }
    }

    .activity-actions {
      padding: 8px 16px;
      display: flex;
      align-items: center;
      justify-content: center;

      .add-activity-btn {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        line-height: 1;
      }
    }
  }

  .activity-log-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr auto;
    gap: 1px;
    border-bottom: 1px solid #d9d9d9;

    &:last-child {
      border-bottom: none;
    }

    .activity-column {
      padding: 8px 16px;
      border-right: 1px solid #d9d9d9;
      display: flex;
      align-items: center;

      &:last-child {
        border-right: none;
      }

      .ant-form-item {
        margin-bottom: 0;
        width: 100%;

        .ant-form-item-control-input {
          min-height: auto;
        }

        // Make select fill the column width
        .ant-select {
          width: 100%;
        }
      }
    }

    .activity-actions {
      padding: 8px 16px;
      display: flex;
      align-items: center;
      justify-content: center;

      .remove-activity-btn {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        line-height: 1;
        color: #ff4d4f;

        &:hover {
          color: #ff7875;
          background-color: #fff2f0;
        }
      }
    }
  }
`;

export default LeadForm;
